import React from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '../../components/layout/PageHeader';
import { KPICard } from '../../components/ui/KPICard';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { StatusPill } from '../../components/ui/StatusPill';
import { DataTable } from '../../components/ui/DataTable';
import { useAuth } from '../../contexts/AuthContext';
import { UsersIcon, ShieldAlertIcon, AlertTriangleIcon, ClockIcon, FilterIcon, ArrowRightIcon } from 'lucide-react';
const pendingOnboarding = [{
  id: '1',
  name: 'Global Foods Ltd',
  category: 'Packaged FMCG',
  stage: 'Risk Review',
  sla: '2 days overdue'
}, {
  id: '2',
  name: 'TechServe Solutions',
  category: 'IT Services',
  stage: 'Bank Verification',
  sla: 'On track'
}, {
  id: '3',
  name: 'Green Valley Farms',
  category: 'Fresh Produce',
  stage: 'Document Upload',
  sla: 'On track'
}];
export const InternalDashboard: React.FC = () => {
  const {
    user
  } = useAuth();
  const navigate = useNavigate();
  return <div className="max-w-7xl mx-auto space-y-6" data-id="element-401">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-2" data-id="element-402">
        <div data-id="element-403">
          <h1 className="text-2xl font-bold text-text-primary" data-id="element-404">
            Good morning, {user?.name?.split(' ')[0]}
          </h1>
          <p className="text-text-secondary" data-id="element-405">
            Procurement Operations • Enterprise Overview
          </p>
        </div>
        <div className="flex gap-3" data-id="element-406">
          <Button variant="secondary" leftIcon={<FilterIcon className="w-4 h-4" data-id="element-408" />} data-id="element-407">
            Filter: All Categories
          </Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-409">
        <KPICard title="Active Suppliers" value="487" icon={<UsersIcon className="w-5 h-5" data-id="element-411" />} trend="up" trendValue="+12 this month" onClick={() => navigate('/internal/suppliers')} data-id="element-410" />
        <KPICard title="Pending Onboarding" value="12" icon={<ClockIcon className="w-5 h-5" data-id="element-413" />} onClick={() => navigate('/internal/onboarding')} data-id="element-412" />
        <KPICard title="Risk Alerts" value="5" icon={<ShieldAlertIcon className="w-5 h-5 text-status-error" data-id="element-415" />} trend="down" trendValue="Critical" onClick={() => navigate('/internal/risk')} data-id="element-414" />
        <KPICard title="Invoice Exceptions" value="8" icon={<AlertTriangleIcon className="w-5 h-5 text-status-warning" data-id="element-417" />} onClick={() => navigate('/internal/transactions')} data-id="element-416" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-418">
        {/* Onboarding Pipeline */}
        <Card data-id="element-419">
          <div className="flex justify-between items-center mb-4" data-id="element-420">
            <h2 className="text-lg font-semibold text-text-primary" data-id="element-421">
              Suppliers Pending Onboarding
            </h2>
            <Button variant="ghost" size="sm" rightIcon={<ArrowRightIcon className="w-4 h-4" data-id="element-423" />} onClick={() => navigate('/internal/onboarding')} data-id="element-422">
              View Pipeline
            </Button>
          </div>
          <DataTable data={pendingOnboarding} columns={[{
          header: 'Supplier Name',
          accessorKey: 'name'
        }, {
          header: 'Category',
          accessorKey: 'category'
        }, {
          header: 'Stage',
          cell: row => <StatusPill status={row.stage === 'Risk Review' ? 'Review' : 'In Progress'} data-id="element-425" />
        }, {
          header: 'SLA Status',
          cell: row => <span className={`text-sm ${row.sla.includes('overdue') ? 'text-status-error font-medium' : 'text-text-secondary'}`} data-id="element-426">
                    {row.sla}
                  </span>
        }]} onRowClick={() => navigate('/internal/onboarding')} data-id="element-424" />
        </Card>

        {/* Action Center */}
        <Card data-id="element-427">
          <h2 className="text-lg font-semibold text-text-primary mb-4" data-id="element-428">
            Review Queues (Action Required)
          </h2>
          <div className="space-y-3" data-id="element-429">
            <div className="p-4 border border-border-light rounded-fluent flex justify-between items-center hover:bg-surface-secondary cursor-pointer" onClick={() => navigate('/internal/queues')} data-id="element-430">
              <div data-id="element-431">
                <h3 className="font-medium text-text-primary" data-id="element-432">
                  Bank Detail Changes
                </h3>
                <p className="text-sm text-text-secondary" data-id="element-433">
                  3 requests pending maker-checker approval
                </p>
              </div>
              <div className="w-8 h-8 rounded-full bg-status-warning-bg text-status-warning flex items-center justify-center font-bold" data-id="element-434">
                3
              </div>
            </div>
            <div className="p-4 border border-border-light rounded-fluent flex justify-between items-center hover:bg-surface-secondary cursor-pointer" onClick={() => navigate('/internal/queues')} data-id="element-435">
              <div data-id="element-436">
                <h3 className="font-medium text-text-primary" data-id="element-437">
                  Compliance Document Renewals
                </h3>
                <p className="text-sm text-text-secondary" data-id="element-438">
                  7 documents uploaded for review
                </p>
              </div>
              <div className="w-8 h-8 rounded-full bg-status-info-bg text-status-info flex items-center justify-center font-bold" data-id="element-439">
                7
              </div>
            </div>
            <div className="p-4 border border-border-light rounded-fluent flex justify-between items-center hover:bg-surface-secondary cursor-pointer" onClick={() => navigate('/internal/queues')} data-id="element-440">
              <div data-id="element-441">
                <h3 className="font-medium text-text-primary" data-id="element-442">
                  Contract Redlines
                </h3>
                <p className="text-sm text-text-secondary" data-id="element-443">
                  2 contracts pending legal review
                </p>
              </div>
              <div className="w-8 h-8 rounded-full bg-status-error-bg text-status-error flex items-center justify-center font-bold" data-id="element-444">
                2
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>;
};